import type jsPDF from 'jspdf';
import {
  enrichQueueCallsWithAgentMisses,
  fetchExtensions,
  fetchQueueCallDetails,
  fetchQueueDetail,
  fetchQueues,
} from '@/services/api';
import type { Extension, QueueCallDetail, QueueDetail } from '@/types';
import { BrandPdf, PDF_COLORS, type PdfValue } from '@/lib/pdf/brandPdf';
import type { PdfAssets } from '@/lib/pdf/pdfAssets';
import { formatGenerated, formatDay } from './callCostReport';
import { csvText } from './extensionReports';

/** The Queue Missed report's data, CSV and PDF, for the Reports page and scheduled reports. */

export function toYeastarDateTime(date: string, endOfDay: boolean) {
  return `${date.replace(/-/g, '/')} ${endOfDay ? '23:59:59' : '00:00:00'}`;
}

export function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return s ? `${m}m ${s}s` : `${m}m`;
}

/** Normalize Yeastar status values (they use "abandond" spelling). */
export function normalizeStatus(status: string): 'answered' | 'missed' | 'abandoned' | 'other' {
  const s = String(status || '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
  if (s.includes('abandon') || s === 'abandond') return 'abandoned';
  if (s.includes('miss') || s.includes('noanswer') || s === 'noanswered' || s === 'noansw') {
    return 'missed';
  }
  if (s.includes('answer')) return 'answered';
  return 'other';
}

export function outcomeLabel(status: string): string {
  const n = normalizeStatus(status);
  if (n === 'answered') return 'Answered';
  if (n === 'missed') return 'No one answered';
  if (n === 'abandoned') return 'Caller hung up';
  return status || '—';
}

export function outcomeHelp(status: string, waitSeconds?: number, maxWait?: number | string): string {
  const n = normalizeStatus(status);
  if (n === 'abandoned') {
    return 'The caller left the queue before an agent took the call.';
  }
  if (n === 'missed') {
    const max = Number(maxWait);
    if (waitSeconds != null && Number.isFinite(max) && max > 0 && waitSeconds + 3 < max) {
      return 'Agents were offered the call but nobody answered (left queue before max wait).';
    }
    return 'Nobody answered while the call was in queue.';
  }
  if (n === 'answered') return 'An agent answered the call.';
  return '';
}

export function isMissOrAbandon(row: QueueCallDetail): boolean {
  const n = normalizeStatus(row.status);
  return n === 'missed' || n === 'abandoned';
}

export function formatTimeout(value?: number | string): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  if (n <= 0) return '—';
  return formatDuration(n);
}

function extensionDisplayName(ext: Extension): string {
  return (
    ext.display_name ||
    [ext.first_name, ext.last_name].filter(Boolean).join(' ').trim() ||
    ext.username ||
    ext.caller_id_name ||
    ''
  );
}

/**
 * Yeastar fail_dest_value is often an internal extension/queue ID, not the
 * dial number — resolve via extension/queue maps when possible.
 */
export function resolveFailoverLabel(
  type?: string,
  value?: string,
  extById?: Map<string, Extension>,
  extByNumber?: Map<string, Extension>,
  queueById?: Map<string, QueueDetail>,
  queueByNumber?: Map<string, QueueDetail>
): string {
  const t = (type || '').trim().toLowerCase();
  const v = (value || '').trim();
  if (!t && !v) return '—';
  if (t === 'end_call' || t === 'hang_up' || t.includes('hangup')) return 'Hang up';
  if (t === 'play_greeting') return v ? `Play greeting (${v})` : 'Play greeting';
  if (t === 'play_prompt') return v ? `Play prompt (${v})` : 'Play prompt';
  if (t === 'external_num') return v ? `External ${v}` : 'External number';
  if (t === 'dial_by_name') return 'Dial by name';

  if (t === 'extension' || t === 'ext_vm' || t.startsWith('ext')) {
    const ext = extById?.get(v) || extByNumber?.get(v);
    if (ext) {
      const name = extensionDisplayName(ext);
      const base = name ? `${name} (${ext.number})` : ext.number;
      return t === 'ext_vm' ? `Voicemail ${base}` : base;
    }
    // Don't show bare internal IDs as if they were extension numbers
    return v ? `Extension ID ${v}` : 'Extension';
  }

  if (t === 'queue' || t.includes('queue')) {
    const q = queueById?.get(v) || queueByNumber?.get(v);
    if (q) {
      if (q.name && q.number && q.name !== q.number) return `${q.name} (${q.number})`;
      return q.number || q.name || v;
    }
    return v ? `Queue ${v}` : 'Queue';
  }

  if (t === 'ivr' || t.includes('ivr')) return v ? `IVR ${v}` : 'IVR';
  if (t === 'ring_group' || t.includes('ring')) return v ? `Ring group ${v}` : 'Ring group';

  if (v) {
    const ext = extById?.get(v) || extByNumber?.get(v);
    if (ext) {
      const name = extensionDisplayName(ext);
      return name ? `${name} (${ext.number})` : ext.number;
    }
  }

  const typeLabel = (type || '').replace(/_/g, ' ');
  if (typeLabel && v) return `${typeLabel} ${v}`;
  return typeLabel || v || '—';
}

export function formatFailover(q?: QueueDetail | null): string {
  if (!q) return '—';
  if (q.failDestLabel) return q.failDestLabel;
  const type = (q.failDestType || '').trim();
  const value = (q.failDestValue || '').trim();
  if (!type && !value) return '—';
  return resolveFailoverLabel(type, value);
}

export function formatAgents(q?: QueueDetail | null): string {
  if (!q?.agents?.length) return '—';
  return q.agents.map((a) => (a.name ? `${a.number} ${a.name}` : a.number)).join(', ');
}

export function lookupQueueConfig(
  row: QueueCallDetail,
  byId: Map<string, QueueDetail>,
  byNumber: Map<string, QueueDetail>,
  byName: Map<string, QueueDetail>
): QueueDetail | undefined {
  // Prefer matching by queue number/name from the call row
  if (row.queue_num && byNumber.has(row.queue_num)) return byNumber.get(row.queue_num);
  const nameKey = (row.queue_name || '').toLowerCase().trim();
  if (nameKey && byName.has(nameKey)) return byName.get(nameKey);
  // Fallback: any single match if only one queue selected
  if (byId.size === 1) return byId.values().next().value;
  return undefined;
}

/** Finds each call's queue settings by number or name. */
export function queueConfigResolver(configById: Map<string, QueueDetail>) {
  const byNumber = new Map<string, QueueDetail>();
  const byName = new Map<string, QueueDetail>();
  for (const q of configById.values()) {
    if (q.number) byNumber.set(q.number, q);
    if (q.name) byName.set(q.name.toLowerCase().trim(), q);
  }
  return (row: QueueCallDetail) => lookupQueueConfig(row, configById, byNumber, byName);
}

export interface QueueMissedData {
  rows: QueueCallDetail[];
  configById: Map<string, QueueDetail>;
}

/**
 * Queue calls in the period, with each queue's settings (failover IDs resolved to names), the
 * agents who were rung, and "what happened" saying where the call went when nobody answered.
 * No queue ids means every queue.
 */
export async function loadQueueMissed(input: {
  startDate: string;
  endDate: string;
  queueIds: string[];
  abandonTime?: number;
}): Promise<QueueMissedData> {
  const queueIds = input.queueIds.length ? input.queueIds : (await fetchQueues(false)).map((q) => q.id);
  if (queueIds.length === 0) return { rows: [], configById: new Map() };

  // Load queue config + extensions so failover IDs resolve to real numbers.
  const configMap = new Map<string, QueueDetail>();
  const [extensions] = await Promise.all([
    fetchExtensions(false),
    Promise.all(
      queueIds.map(async (id) => {
        const detail = await fetchQueueDetail(id);
        if (detail) configMap.set(detail.id || id, detail);
      })
    ),
  ]);

  const extById = new Map<string, Extension>();
  const extByNumber = new Map<string, Extension>();
  for (const ext of extensions) {
    if (ext.id != null && String(ext.id) !== '') extById.set(String(ext.id), ext);
    if (ext.number) extByNumber.set(String(ext.number), ext);
  }
  const queueByNumber = new Map<string, QueueDetail>();
  for (const q of configMap.values()) {
    if (q.number) queueByNumber.set(String(q.number), q);
  }
  // Second pass: resolve failover IDs now that all maps are complete
  for (const q of configMap.values()) {
    if (q.failDestType || q.failDestValue) {
      q.failDestLabel = resolveFailoverLabel(q.failDestType, q.failDestValue, extById, extByNumber, configMap, queueByNumber);
    }
  }

  const startTime = toYeastarDateTime(input.startDate, false);
  const endTime = toYeastarDateTime(input.endDate, true);
  let data = await fetchQueueCallDetails({ startTime, endTime, queueIds, abandonTime: input.abandonTime });

  // Attach agents who were rung (Agent Missed Call Activity) when available.
  const queueIdByKey = new Map<string, string>();
  for (const q of configMap.values()) {
    if (q.id) queueIdByKey.set(q.id, q.id);
    if (q.number) queueIdByKey.set(q.number, q.id);
    if (q.name) queueIdByKey.set(q.name.toLowerCase().trim(), q.id);
  }
  try {
    data = await enrichQueueCallsWithAgentMisses(data, queueIdByKey, startTime, endTime);
  } catch (e) {
    console.warn('Agent miss enrichment skipped:', e);
  }

  // Enrich "what happened" with failover destination when nobody answered.
  // Note: queue miss after timeout ≠ failover failed (see Failover Follow-through report).
  const resolveConfig = queueConfigResolver(configMap);
  const rows = data.map((row) => {
    const cfg = resolveConfig(row);
    const failover = formatFailover(cfg);
    const st = normalizeStatus(row.status);
    const maxWait = Number(cfg?.maxWaitTime);
    const wait = row.ring_duration || 0;
    const hitMaxWait = Number.isFinite(maxWait) && maxWait > 0 && wait + 3 >= maxWait;
    let what = row.what_happened || '';
    if (cfg && failover !== '—' && (st === 'missed' || st === 'abandoned') && what && !/failover|then went to/i.test(what)) {
      if (st === 'missed' && hitMaxWait) {
        what = `${what} Queue max wait (${formatDuration(maxWait)}) was reached; failover is set to: ${failover}. Queue still shows missed even if failover later answered — use Failover Follow-through for the overflow leg.`;
      } else if (st === 'missed') {
        what = `${what} Wait was ${formatDuration(wait)} (queue max wait ${formatTimeout(cfg.maxWaitTime)}). Failover is configured as: ${failover}.`;
      } else {
        what = `${what} If max wait is reached, failover is set to: ${failover}.`;
      }
    }
    return { ...row, what_happened: what };
  });
  return { rows, configById: configMap };
}

/** The rows shown: missed and abandoned only (or every call), and the caller search. */
export function filterQueueRows(rows: QueueCallDetail[], statusFilter: 'missed' | 'all', callFrom?: string) {
  let list = statusFilter === 'missed' ? rows.filter(isMissOrAbandon) : rows;
  const query = callFrom?.trim().toLowerCase();
  if (query) list = list.filter((r) => (r.call_from || '').toLowerCase().includes(query));
  return list;
}

export function summarizeQueueRows(rows: QueueCallDetail[], statusFilter: 'missed' | 'all', shown: number) {
  const source = statusFilter === 'missed' ? rows.filter(isMissOrAbandon) : rows;
  return {
    total: source.length,
    missed: source.filter((r) => normalizeStatus(r.status) === 'missed').length,
    abandoned: source.filter((r) => normalizeStatus(r.status) === 'abandoned').length,
    answered: source.filter((r) => normalizeStatus(r.status) === 'answered').length,
    shown,
  };
}

export function queueMissedCsv(rows: QueueCallDetail[], resolveConfig: (row: QueueCallDetail) => QueueDetail | undefined) {
  return csvText(
    [
      'Time',
      'Queue',
      'Queue Number',
      'Call From',
      'Outcome',
      'Wait / ring time (s)',
      'Queue agents',
      'Ring strategy',
      'Timeout (s)',
      'Failover destination',
      'What happened',
      'ID',
    ],
    rows.map((r) => {
      const cfg = resolveConfig(r);
      return [
        r.time,
        r.queue_name,
        r.queue_num || '',
        r.call_from,
        outcomeLabel(r.status),
        r.ring_duration,
        formatAgents(cfg),
        cfg?.ringStrategy || '',
        cfg?.maxWaitTime ?? '',
        formatFailover(cfg),
        r.what_happened || '',
        r.id,
      ];
    })
  );
}

const c = PDF_COLORS;

const outcomeColor = (status: string) => {
  const n = normalizeStatus(status);
  return n === 'answered' ? c.green : n === 'abandoned' ? c.orange : n === 'missed' ? c.red : c.muted;
};

export function queueMissedPdf(input: {
  rows: QueueCallDetail[];
  allRows: QueueCallDetail[];
  statusFilter: 'missed' | 'all';
  filtersText: string[];
  resolveConfig: (row: QueueCallDetail) => QueueDetail | undefined;
  startDate: string;
  endDate: string;
  customerName?: string;
  assets?: PdfAssets;
  now?: Date;
}): jsPDF {
  const { rows } = input;
  const summary = summarizeQueueRows(input.allRows, input.statusFilter, rows.length);
  const doc = new BrandPdf({
    orientation: 'landscape',
    assets: input.assets,
    reportName: 'Queue missed calls',
    title: input.statusFilter === 'all' ? 'Queue calls' : 'Missed and abandoned queue calls',
    subtitle: `${formatDay(input.startDate)} – ${formatDay(input.endDate)}  ·  ${input.filtersText.join('  ·  ')}`,
    customerName: input.customerName,
    generated: `Generated ${formatGenerated(input.now ?? new Date())}`,
  });
  doc.statTiles([
    { label: 'Calls listed', value: String(rows.length), caption: input.statusFilter === 'all' ? 'All queue calls' : 'Missed or abandoned', featured: true },
    { label: 'No one answered', value: String(summary.missed), dot: c.red, caption: 'Agents rung, nobody picked up' },
    { label: 'Caller hung up', value: String(summary.abandoned), dot: c.orange, caption: 'Left before an agent answered' },
    ...(input.statusFilter === 'all'
      ? [{ label: 'Answered', value: String(summary.answered), dot: c.green, caption: 'Taken by an agent' }]
      : []),
  ]);

  const wait = (row: QueueCallDetail): PdfValue => formatDuration(row.ring_duration || 0);
  doc.table({
    title: 'Calls',
    meta: `${rows.length} call${rows.length === 1 ? '' : 's'}, newest first`,
    columns: [
      { header: 'Time', width: 30 },
      { header: 'Queue', width: 28 },
      { header: 'Caller', width: 30 },
      { header: 'Outcome', width: 27 },
      { header: 'Wait', width: 14, align: 'right' },
      { header: 'Agents who missed', width: 36, wrap: true },
      { header: 'Failover', width: 28 },
      { header: 'What happened', width: 87, wrap: true },
    ],
    rows: [...rows]
      .sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0) || b.time.localeCompare(a.time))
      .map((row) => [
        row.time,
        row.queue_num && row.queue_name !== row.queue_num ? `${row.queue_name} (${row.queue_num})` : row.queue_name,
        row.call_from || '—',
        { text: outcomeLabel(row.status), color: outcomeColor(row.status), badge: true },
        wait(row),
        (row.agents_who_missed ?? []).join(', ') || '—',
        formatFailover(input.resolveConfig(row)),
        row.what_happened || outcomeHelp(row.status, row.ring_duration, input.resolveConfig(row)?.maxWaitTime),
      ]),
  });
  return doc.finish();
}
